import React from 'react';
import StarRating from '../StarRating/StarRating';
import RiskRewardGauge from '../RiskReward/RiskReward';
import { MFBeaconButton } from '../Beacon/BeaconComponents';
import {
  NA_VALUE,
  cashLikeFunds,
  iClassAsterix,
  normalizeValue,
  withAnnotation
} from './util';
import { isEmpty } from '../../core/util';
import { formatDate } from '../../flows/shared/dateUtils';
import addFundBtn from '../../images/icon_plus_buy_button.svg';
import addedFundBtn from '../../images/green-checkmark.svg';
import selectFundsRibbon from '../../images/select-funds-ribbon.svg';
import unlockIcon from '../../images/blue-unlock.svg';
import lockIcon from '../../images/gray-lock.svg';
import styles from './FundListItem.css';
import { generateGeneralTestID } from '../../generateAutomationTestIDs';

// I CLASS funds should utilize the `tackedReturnFundInceptionDate` field whenever present
// otherwise default to using the `inceptionDate`
// MFRT REF - https://gitlab.awstrp.net/mfresearchtool/mfresearchtool-ui/-/blob/master/src/utils/fund-info.js#L374
// MFRT USAGE - https://gitlab.awstrp.net/mfresearchtool/mfresearchtool-ui/-/blob/master/src/components/Summary/util.js#L328
const getInceptionDate = fund =>
  `(${formatDate(
    fund.iClass &&
      !isEmpty(
        fund.standardPerformanceReturns?.quarterlyAnnualReturns?.fundPerformance
          ?.sinceInceptionReturnTacked
      )
      ? fund.tackedReturnFundInceptionDate
      : fund.inceptionDate,
    'MM/DD/YYYY'
  )})`;

export const inceptionData = (fund = {}) => {
  const fundSinceInceptionReturn =
    fund?.standardPerformanceReturns?.quarterlyAnnualReturns?.fundPerformance
      ?.sinceInceptionReturn;
  if (!fundSinceInceptionReturn || fundSinceInceptionReturn === NA_VALUE) {
    return '-';
  }

  return `${fundSinceInceptionReturn}%\n${getInceptionDate(fund)}`;
};

export const showReturn = (year, fund = {}) => {
  const firstYearReturn =
    fund?.standardPerformanceReturns?.quarterlyAnnualReturns?.fundPerformance
      ?.oneYearReturn;
  const fifthYearReturn =
    fund?.standardPerformanceReturns?.quarterlyAnnualReturns?.fundPerformance
      ?.fiveYearReturn;
  const tenthYearReturn =
    fund?.standardPerformanceReturns?.quarterlyAnnualReturns?.fundPerformance
      ?.tenYearReturn;

  const returnMap = {
    1: withAnnotation(normalizeValue(firstYearReturn, true)),
    5: withAnnotation(normalizeValue(fifthYearReturn, true)),
    10: withAnnotation(normalizeValue(tenthYearReturn, true)),
    default: '-'
  };

  return returnMap[year] || returnMap.default;
};

const fundReferenceMap = {
  TUHYX: '***',
  RPIEX: '++',
  RPEIX: '++'
};

const moneyMarketReferenceMap = {
  Retail: '‡',
  Government: '‡‡'
};

export const Reference = symbol => Boolean(symbol) && <span>{symbol}</span>;
const FundReference = ticker => Reference(fundReferenceMap[ticker]);
const MoneyMarketReference = marketType =>
  Reference(moneyMarketReferenceMap[marketType]);

const References = ({ fund }) => (
  <>
    {iClassAsterix(fund) && <span>*</span>}
    <MoneyMarketReference ticker={fund.moneyMarketType} />
    <FundReference ticker={fund.ticker} />
    {cashLikeFunds.indexOf(fund.ticker) !== -1 && <span>+</span>}
  </>
);
/* eslint-disable */
/* Completexity is too high for this component - needs to be lowered from 6 to 5 */
const FundName = ({ fund, onTitleClick, disabled }) => {
  return (
    <>
      <td className="fund-icon-container">
        <div className="fund-icon" tabIndex="0">
          {disabled && (
            <img
              className="byop-fundlist-sf-icon"
              src={lockIcon}
              alt="Locked"
            />
          )}
          {!disabled && fund.unlockedFund && (
            <img
              className="byop-fundlist-sf-icon"
              src={unlockIcon}
              alt="Unlocked"
            />
          )}
          {fund.selectFund && (
            <img
              className="byop-fundlist-sf-icon"
              src={selectFundsRibbon}
              alt="Select Fund Certified"
            />
          )}
        </div>
      </td>
      <td id="fundName">
        <button
          type="button"
          data-name="fundNameBtn"
          className="fund-name btn-clear-bg"
          onClick={() => onTitleClick(fund.ticker)}
          tabIndex="0">
          <div className="fund-icon-name-container">
            <div className="fund-name-with-ticker">
              <span className="name">
                {fund.name}
                <References fund={fund} />
              </span>{' '}
              <span id="tickerName" className="ticker">
                ({fund.ticker || ''})
              </span>
              {fund.isClosedFund && (
                <div className="redemption-fee">
                  <div>CLOSED FUND</div>
                </div>
              )}
            </div>
          </div>
        </button>
      </td>
      <style jsx>{styles}</style>
    </>
  );
};
/* eslint-enable */
const MorningstarRating = ({ fund }) => (
  <td id="morningStarOverallRating">
    {fund?.morningStarRatings?.morningStarOverallRating > 0 && (
      <span>
        {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={`https://www.troweprice.com/personal-investing/tools/fund-research/${fund.ticker}`}>
          <StarRating
            numOfStars={5}
            rating={fund?.morningStarRatings?.morningStarOverallRating}
            ratedColor="#006894"
            nonRatedColor="#B4BABF"
          />
        </a>
        <span className="fund-row-text star-text">
          (out of {fund?.morningStarRatings?.morningStarOverallQuantity} funds)
        </span>
      </span>
    )}
    <style jsx>{styles}</style>
  </td>
);

const SubAssetClass = ({ fund }) => (
  <td id="morningStarCategoryName" className="sub-asset-class">
    {fund?.morningStarRatings?.morningStarCategoryName}
    <style jsx>{styles}</style>
  </td>
);

const RiskReward = ({ fund, toggleRiskRewardModal }) => (
  <td id="riskLevel">
    {fund.riskLevel > 0 && (
      <RiskRewardGauge
        toggleRiskRewardModal={toggleRiskRewardModal}
        fundRiskReward={fund.riskLevel}
        name={fund.name}
        ticker={fund.ticker}
      />
    )}
  </td>
);

const buildYearReturn = ({ fund, reportType }) => ({ year, sinceInception }) =>
  reportType === 'average' && (
    <td
      id={`${sinceInception ? 'sinceInceptionReturn' : `${year}YearReturn`}`}
      className={`fund-row-text ${
        sinceInception ? 'since-inception' : 'average-returns'
      }`}>
      {sinceInception ? inceptionData(fund) : showReturn(year, fund)}
      <style jsx>{styles}</style>
    </td>
  );

const buildYield = ({ reportType }) => ({ value, moneyMarket = false }) =>
  reportType === 'yield' && (
    <td id="sevenDayYieldSimple" className="fund-row-text average-returns">
      {normalizeValue(moneyMarket && value, true)}
      <style jsx>{styles}</style>
    </td>
  );

const ExpenseRatio = ({ fund }) => (
  <td id="grossExpenseRatio" className="fund-row-text expense-ratio">
    {normalizeValue(fund.grossExpenseRatio, true)}
    <style jsx>{styles}</style>
  </td>
);

export const DisabledButton = () => (
  <td>
    <MFBeaconButton
      disabled
      id="closedFundBtn"
      type="button"
      variant="secondary"
      label="Closed"
    />
    <style jsx>{styles}</style>
  </td>
);

export const AddButton = ({ fund, disabled, addFund }) => (
  <td className="link">
    <MFBeaconButton
      onClick={() =>
        !disabled &&
        addFund({
          name: fund.name,
          ticker: fund.ticker,
          amount: 0,
          percent: 0,
          unlockedFund: fund.unlockedFund
        })
      }
      id="addFundToCartBYOP"
      data-name={generateGeneralTestID('BYOP', `AddFundButton_${fund.ticker}`)}
      disabled={disabled}
      variant="secondary"
      icon-align="left"
      label="Add">
      <img
        slot="icon"
        src={addFundBtn}
        alt="Add Fund"
        id={`Add Fund ${fund.ticker}`}
      />
    </MFBeaconButton>
    <style jsx>{styles}</style>
  </td>
);

const AddedButton = ({ fund, removeFund }) => (
  <td className="link">
    <MFBeaconButton
      onClick={() => removeFund(fund.ticker)}
      id="removeFundFromCartBYOP"
      data-name={generateGeneralTestID(
        'BYOP',
        `RemoveFundButton_${fund.ticker}`
      )}
      type="button"
      variant="tertiary"
      icon-align="left"
      label="Added">
      <img
        slot="icon"
        src={addedFundBtn}
        alt="Added Fund"
        id={`Added Fund ${fund.ticker}`}
      />
    </MFBeaconButton>
    <style jsx>{styles}</style>
  </td>
);

const BuyButtonRenderer = ({
  fund,
  isSelected,
  addFund,
  removeFund,
  disabled,
  fundAdded
}) => {
  return (
    <>
      {disabled && <DisabledButton />}

      {fundAdded(fund.ticker) && (
        <AddedButton fund={fund} removeFund={removeFund} />
      )}

      {!disabled && !fundAdded(fund.ticker) && (
        <AddButton
          fund={fund}
          isSelected={isSelected}
          addFund={addFund}
          disabled={disabled}
        />
      )}
    </>
  );
};

const FundListItem = ({
  fund,
  disabled,
  isQuickViewOpen,
  isSelected,
  onTitleClick,
  reportType,
  toggleRiskRewardModal,
  addFund,
  removeFund,
  fundAdded
}) => {
  const YearReturn = buildYearReturn({ fund, reportType });
  const Yield = buildYield({ reportType });

  const {
    sevenDayYieldSimple,
    unsubsidizedSevenDayYieldSimple,
    moneyMarket
  } = fund;

  return (
    <tr
      className={`fund-row${isQuickViewOpen ? ' no-border' : ''}${
        disabled ? ' disabled' : ''
      }`}>
      {/* <FundIcon fund={fund} disabled={disabled} /> */}

      <FundName fund={fund} onTitleClick={onTitleClick} disabled={disabled} />

      <MorningstarRating fund={fund} />

      <SubAssetClass fund={fund} />

      <RiskReward toggleRiskRewardModal={toggleRiskRewardModal} fund={fund} />

      <YearReturn year={1} />

      <YearReturn year={5} />

      <YearReturn year={10} />

      <YearReturn sinceInception />

      <Yield value={sevenDayYieldSimple} moneyMarket={moneyMarket} />

      <Yield
        value={unsubsidizedSevenDayYieldSimple}
        moneyMarket={moneyMarket}
      />

      {/* US332249 - Remove compound column for yield report */}
      {reportType === 'yield' && (
        <>
          {/* eslint-disable jsx-a11y/control-has-associated-label */}
          <td />
          <td />
        </>
      )}

      <ExpenseRatio fund={fund} />

      <BuyButtonRenderer
        fund={fund}
        isSelected={isSelected}
        addFund={addFund}
        removeFund={removeFund}
        disabled={disabled}
        fundAdded={fundAdded}
      />

      <style jsx>{styles}</style>
    </tr>
  );
};

export default FundListItem;
